import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// The Context
const CustomThemeContext = createContext();

export const useCustomTheme = () => useContext(CustomThemeContext);

export const CustomThemeProvider = ({ children }) => {
  const [primaryColor, setPrimaryColor] = useState('#1976d2');
  const [mode, setMode] = useState('light');

  // Load from local storage initially
  useEffect(() => {
    const savedColor = localStorage.getItem('tenant_primaryColor');
    const savedMode = localStorage.getItem('tenant_mode');
    
    if (savedColor) setPrimaryColor(savedColor);
    if (savedMode) setMode(savedMode);
  }, []);

  // Sync to local storage
  const updateTheme = (newColor, newMode) => {
    setPrimaryColor(newColor);
    setMode(newMode);
    localStorage.setItem('tenant_primaryColor', newColor);
    localStorage.setItem('tenant_mode', newMode);
  };

  const getContrastText = (hexcolor) => {
    if (!hexcolor) return '#ffffff';
    hexcolor = hexcolor.replace("#", "");
    if (hexcolor.length === 3) {
      hexcolor = hexcolor.split('').map(c => c + c).join('');
    }
    const r = parseInt(hexcolor.substr(0, 2), 16);
    const g = parseInt(hexcolor.substr(2, 2), 16);
    const b = parseInt(hexcolor.substr(4, 2), 16);
    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    // Return dark gray for bright backgrounds, white for dark backgrounds
    return (yiq >= 128) ? '#1e293b' : '#ffffff';
  };

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: primaryColor,
          },
          secondary: {
            main: '#dc004e',
          },
          background: {
            default: mode === 'light' ? '#f5f6fa' : '#0f172a',
            paper: mode === 'light' ? '#ffffff' : '#1e293b',
          },
        },
        typography: {
          fontFamily: '"Outfit", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        },
        components: {
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
              }
            }
          }
        }
      }),
    [mode, primaryColor]
  );

  return (
    <CustomThemeContext.Provider value={{ primaryColor, mode, updateTheme, getContrastText }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </CustomThemeContext.Provider>
  );
};
